import React, { useMemo, useEffect, useState, useCallback } from "react";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Box, TableSortLabel } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import CartTableRowEposSales from "./CartTableRowEposSales";
import CartTableRowSkeleton from "../../Epos/component/CartTableRowSkeleton";
import { applyShowPromoLabelDedupe } from "../../Epos/utils/cartCalculations";
import { isCartLineOfferDisplayRole } from "../../Promotion/utils/promotionOfferProductsShared";
import { useCartTableKeyboardNavigation, isInsideModalDialog, isOfferProductsDialogOpen } from "../../Epos/utils/cartTableKeyboard";
import commonStyles from "../../../common/commonStyles.css";

const getCartLineCode = (item) =>
  String(item?.code || item?.product_code || "").trim().toLowerCase();

const getCartLineDescription = (item) =>
  String(item?.desc || item?.name || item?.product_name || "").trim().toLowerCase();

const CartTableEposSales = ({
  cartItems,
  onTypeChange,
  onQuantityChange,
  onPriceChange,
  onDiscountChange,
  onDeleteItem,
  showVatAsAmount = false,
  isLoading = false,
  getItemDetails = null,
  isItemPending = null,
  onValidationError = null,
  customerId = null,
  onAllotQtyChange = null,
  onQuantityPopupClose = null,
  onQuantityPopupOpen = null,
  onDiscountChangeComplete = null,
  onDiscountCommentChange = null,
  newlyAddedProductId = null,
  enableProductSplit = false,
  isTelecaller = false,
  onPromoQtyLineOpenDrawer = null,
  onPromoQtyOpenPromotionsModal = null,
  cartItemsForQtyMax = null,
  bundleDiscounts = [],
  bundleLineDiscountByKey = null,
}) => {
  const isRhp = localStorage.getItem('accessToken') === 'rhp' || localStorage.getItem('accessToken') === 'hp';
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const handleSort = useCallback((columnId) => {
    if (!isTelecaller) return;
    setSortBy((prev) => {
      if (prev === columnId) {
        setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
        return prev;
      }
      setSortDirection("asc");
      return columnId;
    });
  }, [isTelecaller]);

  const baseRows = useMemo(
    () => applyShowPromoLabelDedupe(Array.isArray(cartItems) ? cartItems : [], bundleDiscounts),
    [cartItems, bundleDiscounts]
  );

  const keyboardRows = useMemo(() => {
    if (!isTelecaller || !sortBy) return baseRows;
    const sorted = [...baseRows];
    sorted.sort((a, b) => {
      const aVal = sortBy === "code" ? getCartLineCode(a) : getCartLineDescription(a);
      const bVal = sortBy === "code" ? getCartLineCode(b) : getCartLineDescription(b);
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [baseRows, isTelecaller, sortBy, sortDirection]);

  const rowCount = keyboardRows.length;
  const [keyboardRowIndex, setKeyboardRowIndex] = useCartTableKeyboardNavigation(rowCount);

  useEffect(() => {
    if (rowCount === 0 || keyboardRowIndex === null) return;
    const active = document.activeElement;
    if (!(active instanceof Element)) return;
    if (isOfferProductsDialogOpen()) return;
    if (isInsideModalDialog(active)) return;
    const fromQty = active.closest("[data-cart-qty-scope]");
    const fromRow = active.closest("[data-cart-row]");
    let prevIdx = null;
    if (fromQty) {
      prevIdx = Number(fromQty.getAttribute("data-cart-qty-scope"));
    } else if (fromRow) {
      prevIdx = Number(fromRow.getAttribute("data-cart-row"));
    }
    if (!Number.isFinite(prevIdx) || prevIdx === keyboardRowIndex) return;
    const nextScope = document.querySelector(`[data-cart-qty-scope="${keyboardRowIndex}"]`);
    if (!nextScope) return;
    const first = nextScope.querySelector('button:not([disabled]), [tabindex="0"], [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
    requestAnimationFrame(() => first?.focus?.());
  }, [rowCount, keyboardRowIndex]);

  useEffect(() => {
    if (rowCount === 0 || keyboardRowIndex === null) return;
    const onKeyDown = (e) => {
      if (e.key !== "Tab" || e.shiftKey) return;
      const active = document.activeElement;
      if (!(active instanceof Element)) return;
      if (isOfferProductsDialogOpen()) return;
    if (isInsideModalDialog(active)) return;
      const rowEls = document.querySelectorAll(`[data-cart-row="${keyboardRowIndex}"]`);
      const insideHighlightedRow = Array.from(rowEls).some((el) => el.contains(active));
      if (insideHighlightedRow) return;
      const qtyScope = document.querySelector(`[data-cart-qty-scope="${keyboardRowIndex}"]`);
      if (!qtyScope) return;
      e.preventDefault();
      const first = qtyScope.querySelector('button:not([disabled]), [tabindex="0"], [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])');
      first?.focus?.();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [rowCount, keyboardRowIndex]);

  const renderedRows = useMemo(() => {
    return keyboardRows.map((item, index) => {
      const itemDetails = getItemDetails ? getItemDetails(item) : null;
      const isPending = isItemPending ? isItemPending(item.product_id, item.id) : false;
      const productId = String(item.product_id || item.id || "");
      const shouldAutoOpen = newlyAddedProductId && String(newlyAddedProductId) === productId;
      const orderType = item.order_type || item.type || "Box";
      const lineId = item?.id != null && String(item.id).trim() !== ""
        ? String(item.id)
        : "";
      const promoRole = isCartLineOfferDisplayRole(item) ? "free" : "buy";
      const stableKey = lineId || `${productId}_${orderType}_${promoRole}_${index}`;
      
      return (
        <CartTableRowEposSales
          key={stableKey}
          isRhp={isRhp}
          item={item}
          index={index}
          isKeyboardHighlighted={keyboardRowIndex !== null && keyboardRowIndex === index}
          onQtyRegionFocusChange={() => setKeyboardRowIndex(index)}
          onTypeChange={onTypeChange}
          onQuantityChange={onQuantityChange}
          onPriceChange={onPriceChange}
          onDiscountChange={onDiscountChange}
          onDelete={onDeleteItem}
          showVatAsAmount={showVatAsAmount}
          isLoading={isPending}
          itemDetails={itemDetails}
          onValidationError={onValidationError}
          customerId={customerId}
          onAllotQtyChange={onAllotQtyChange}
          onQuantityPopupClose={onQuantityPopupClose}
          onQuantityPopupOpen={onQuantityPopupOpen}
          autoOpenQuantityPopup={shouldAutoOpen}
          onDiscountChangeComplete={onDiscountChangeComplete}
          onDiscountCommentChange={onDiscountCommentChange}
          enableProductSplit={enableProductSplit}
          isTelecaller={isTelecaller}
          onPromoQtyLineOpenDrawer={onPromoQtyLineOpenDrawer}
          onPromoQtyOpenPromotionsModal={onPromoQtyOpenPromotionsModal}
          cartItemsForQtyMax={keyboardRows}
          bundleDiscounts={bundleDiscounts}
          bundleLineDiscountByKey={bundleLineDiscountByKey}
        />
      );
    });
  }, [keyboardRows, getItemDetails, isItemPending, newlyAddedProductId, keyboardRowIndex, onTypeChange, onQuantityChange, onPriceChange, onDiscountChange, onDeleteItem, showVatAsAmount, onValidationError, customerId, onAllotQtyChange, onQuantityPopupClose, onQuantityPopupOpen, onDiscountChangeComplete, onDiscountCommentChange, enableProductSplit, isTelecaller, onPromoQtyLineOpenDrawer, onPromoQtyOpenPromotionsModal, cartItems, bundleDiscounts, bundleLineDiscountByKey]
  );

  return (
    <TableContainer 
      component={Box} 
      sx={{ 
        mt: 1, 
        mx: { xs: -2, sm: -2, md: -2 }, 
        width: { xs: 'calc(100% + 32px)', sm: 'calc(100% + 32px)', md: 'calc(100% + 32px)' },
        borderRadius: 0, 
        height: 'calc(100vh - 235px)', 
        overflowX: 'auto',
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
          height: '8px',
          width: '8px'
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#f1f1f1'
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#888',
          borderRadius: '4px'
        }
      }}
    >
      <Table 
        size="small"
        className="table-cell-border"
        sx={{ 
          tableLayout: 'fixed',
          width: '100%'
        }}
      >
  
        <TableHead>
          <TableRow className="table-cell-header">
            <TableCell sx={{ width: '50px', textAlign: 'center', }}>No</TableCell>
            <TableCell sx={{ width: '100px', padding: "4px" }}>
              {isTelecaller ? (
                <TableSortLabel
                  active={sortBy === "code"}
                  direction={sortBy === "code" ? sortDirection : "asc"}
                  onClick={() => handleSort("code")}
                  hideSortIcon={false}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1 !important",
                      visibility: "visible !important",
                    },
                  }}
                >
                  Code
                </TableSortLabel>
              ) : (
                "Code"
              )}
            </TableCell>
            <TableCell sx={{ width: '240px', padding: "4px" }}>
              {isTelecaller ? (
                <TableSortLabel
                  active={sortBy === "description"}
                  direction={sortBy === "description" ? sortDirection : "asc"}
                  onClick={() => handleSort("description")}
                  hideSortIcon={false}
                  sx={{
                    "& .MuiTableSortLabel-icon": {
                      opacity: "1 !important",
                      visibility: "visible !important",
                    },
                  }}
                >
                  Product Description
                </TableSortLabel>
              ) : (
                "Product Description"
              )}
            </TableCell>
            <TableCell sx={{ width: '50px', padding: "4px" }}>Type</TableCell>
            <TableCell sx={{ width: '112px', textAlign: 'center' }}>Allot Qty</TableCell>
            {isTelecaller ? (
              <>
                <TableCell sx={{ width: '80px', textAlign: 'right', padding: "4px" }}>Std. Price</TableCell>
                <TableCell sx={{ width: '80px', textAlign: 'right', padding: "4px" }}>Offer Price</TableCell>
              </>
            ) : (
              <TableCell sx={{ width: '82px', textAlign: 'right', padding: "4px" }}>Unit Price</TableCell>
            )}
          <TableCell sx={{ width: '90px', textAlign: 'center', padding: "4px" }}>Item Disc</TableCell>
          {/* <TableCell sx={{ width: '93px', textAlign: 'center', padding: "4px" }}>Item Disc</TableCell> */}

          <TableCell sx={{ width: '70px', textAlign: 'right', padding: "4px" }}>Total</TableCell>
            <TableCell sx={{ width: '60px', textAlign: 'right',padding: "4px" }}>VAT</TableCell>
            <TableCell sx={{ width: '40px', textAlign: 'center', padding: "4px" }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && cartItems.length === 0 ? (
            Array.from({ length: 3 }).map((_, index) => (
              <CartTableRowSkeleton key={index} />
            ))
          ) : cartItems.length === 0 ? (
            <TableRow
               >
              <TableCell colSpan={isTelecaller ? 11 : 10} align="center" sx={{ py: 3, border: 0 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Iconify 
                    icon="solar:cart-large-bold" 
                    width={80} 
                    sx={{ color: 'text.disabled', mb: 2 }} 
                  />
                  <Typography variant="h6" color="text.primary" gutterBottom>
                    No products added yet
                  </Typography>
                  <Typography variant="body2">
                    Use Add New Product to add items
                  </Typography>
                </Box>
              </TableCell>
            </TableRow>
          ) : (
            renderedRows
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default CartTableEposSales;

